import './polyfills';
import './loadConfig';
import React from 'react';
import ReactDOM from 'react-dom';
import 'office-ui-fabric-core/dist/css/fabric.min.css';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ConnectedRouter } from 'connected-react-router';
import { initializeIcons } from 'office-ui-fabric-react/lib/Icons';
import { initializeFileTypeIcons } from '@uifabric/file-type-icons';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import createStore, { getHistory } from './store';
import AuthenticatedApp from './App';
import UnauthenticatedApp from './UnauthenticatedApp';
import UnauthorizedApp from './UnauthorizedApp';
import { getAccount, processAuth, initializeAuth } from './utils/auth';
import MaintenancePage from './pages/MaintenancePage';

try {
  if (process.env.NODE_ENV === 'development') {
    // override config with local development values
    // eslint-disable-next-line
    const config = require('./config.override.js');
    window.config = { ...window.config, ...config };
  }
} catch (err) {
  // do nothing
}

// Setting up app insights here
if (window.config.appInsightsInstrumentationKey) {
  window.appInsights = new ApplicationInsights({
    config: {
      instrumentationKey: window.config.appInsightsInstrumentationKey,
    },
  });
  window.appInsights.loadAppInsights();
  window.appInsights.trackPageView();
}

// Import icons from Fabric-UI
// https://github.com/OfficeDev/office-ui-fabric-react/tree/master/packages/icons
initializeIcons();
initializeFileTypeIcons();

async function main() {
  try {
    await initializeAuth();
    const auth = await processAuth();

    let componentToLoad = <UnauthenticatedApp />;

    if (auth && !auth.loginInProgress && auth.user) {
      componentToLoad = auth.user.isUnauthorized ? <UnauthorizedApp /> : <AuthenticatedApp />;
    }

    if (auth && !auth.loginInProgress && !auth.user?.isUnauthorized && getAccount() != null) {
      if (auth.user && window.config.showMaintenancePage) {
        ReactDOM.render(<MaintenancePage />, document.getElementById('root'));
        return;
      }
      componentToLoad = <AuthenticatedApp />;
    }

    const store = createStore();
    const queryClient = new QueryClient();

    ReactDOM.render(
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <ConnectedRouter history={getHistory()} noInitialPop>
            {componentToLoad}
          </ConnectedRouter>
        </QueryClientProvider>
      </Provider>,
      document.getElementById('root'),
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);

    if (window.appInsights) {
      window.appInsights.trackException({ exception: error });
    }

    ReactDOM.render(<MaintenancePage />, document.getElementById('root'));
  }
}

if (window === window.top) {
  main();
}
